import { useState, type CSSProperties } from 'react';
import { AudioOutlined, DownOutlined, UpOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { Button, Tag } from 'antd';
import { formatCoins } from '../../lib/money';
import { userLabel, userSecondary } from '../../lib/listenerName';
import {
  actorLabel,
  connectionLabel,
  outcomeColor,
  outcomeLabel,
  outcomeReasonLabel,
} from '../calls/callOutcome';

/**
 * Admin → Listener details → Call history, drawn like the listener's own
 * Call history in the app (`history_screen.dart`, listener view): one card per
 * call — who called, how long, the status and what the listener earned — that
 * expands on click into the same "Call details", "Gifts received" and
 * "Call + Gifts total" the listener sees. Amounts are the listener's ₹.
 *
 * Everything arrives WITH the `/admin/calls` list row (the API loads the page's
 * gifts in one query), so expanding makes no request. Gift amounts are the
 * send-time snapshots on `call_gifts` — never today's catalogue price.
 */

/** How a gift was sent. `null` = sent before the source was recorded. */
export type CallGiftSource = 'normal' | 'ask_gift' | null;

export interface CallGiftLine {
  giftCode: string;
  giftName: string;
  emoji: string | null;
  quantity: number;
  unitCoins: number;
  unitEarningPaise: string | null;
  totalCoins: number;
  totalEarningPaise: string | null;
  source: CallGiftSource;
}

/** The fields of an `/admin/calls` row the card uses (optional: older API). */
export interface CallRowDetail {
  id: string;
  userHandle?: string;
  /** The caller's own Display Name — when the API sends it. */
  userDisplayName?: string | null;
  mode: 'audio' | 'video';
  isRandom?: boolean;
  /** Direct call with fallback: this ring's number in its request (1 = picked). */
  attemptNo?: number | null;
  callRequestId?: string | null;
  status: string;
  startedAt?: string | null;
  connectedAt: string | null;
  durationSeconds: number | null;
  totalChargedCoins?: number | null;
  listenerSharePaise?: string;
  disconnectedBy?: string | null;
  disconnectReason?: string | null;
  endedAt?: string | null;
  /** When the listener accepted (null: never accepted). */
  acceptedAt?: string | null;
  /** What happened to the attempt (completed / declined / no_answer / …). */
  outcome?: string | null;
  /** Who performed the terminal action + why (older calls: from the trigger). */
  outcomeActor?: string | null;
  outcomeReason?: string | null;
  /** Raw terminal lifecycle trigger — tells a ring nobody answered apart from
   *  one that never reached the phone. Absent on an older API response. */
  outcomeTrigger?: string | null;
  /** Did the listener's phone acknowledge the ring? (API `ringDelivered`.) */
  ringDelivered?: boolean;
  attributionInferred?: boolean;
  gifts?: CallGiftLine[];
  giftCount?: number;
  giftTotalCoins?: number;
  giftTotalEarningPaise?: string;
  grandTotalCoins?: number;
  grandTotalEarningPaise?: string;
}

export function giftSourceLabel(source: CallGiftSource | undefined): string {
  if (source === 'ask_gift') return 'Ask Gift';
  if (source === 'normal') return 'Normal gift';
  return 'Not recorded';
}

// ── The app's wording (en.json `history.*`), admin point of view ──────────

/** Status line, as the app labels it; anything else shows as-is. */
export function callStatusLabel(status: string): string {
  switch (status) {
    case 'ended_normal':
      return 'Completed';
    case 'ended_low_balance':
      return 'Ran out of balance';
    case 'ended_dropped':
      return 'Connection lost';
    case 'ended_flagged':
      return 'Ended for safety';
    default:
      return status;
  }
}

/** Who ended it. The app says "You" to the listener; the admin names the side. */
export function endedByLabel(by: string | null | undefined): string {
  switch (by) {
    case 'user':
      return 'Caller';
    case 'listener':
      return 'Listener';
    case 'system':
      return 'System';
    case 'admin':
      return 'Support team';
    default:
      return 'Not recorded';
  }
}

const REASON_LABEL: Record<string, string> = {
  hung_up: 'Hung up',
  cancelled: 'Cancelled',
  declined: 'Declined',
  no_answer: 'No answer',
  connect_timeout: "Couldn't connect",
  setup_failed: 'Setup failed',
  network_lost: 'Network lost',
  insufficient_balance: 'Low balance',
  safety_flag: 'Safety',
  admin_force_end: 'Ended by support',
  time_limit: 'Time limit',
};

/** "Listener · Hung up" — the app's Ended-by line; an unknown reason shows raw. */
export function endedByLine(by: string | null | undefined, reason: string | null | undefined): string {
  if (!reason) return endedByLabel(by);
  return `${endedByLabel(by)} · ${REASON_LABEL[reason] ?? reason}`;
}

/** "₹12" / "₹0.34" / "₹2.50" — exactly the app's `formatHistoryRupees`. */
export function appRupees(paise: string | number | null | undefined): string {
  if (paise === null || paise === undefined || paise === '') return '—';
  let p: bigint;
  try {
    p = BigInt(paise);
  } catch {
    return '—';
  }
  const neg = p < 0n;
  if (neg) p = -p;
  const rupees = p / 100n;
  const rest = Number(p % 100n);
  const s = rest === 0 ? `₹${rupees}` : `₹${rupees}.${String(rest).padStart(2, '0')}`;
  return neg ? `-${s}` : s;
}

/** "0m 55s" — the app's duration format. */
export function appDuration(seconds: number | null | undefined): string {
  const s = Math.max(seconds ?? 0, 0);
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/** "28 Sep 2026 · 9:19 AM" in the viewer's zone (admin-only line). */
function when(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date} · ${time.toUpperCase()}`;
}

// ── Look (the app's dark card) ─────────────────────────────────────────────

const C = {
  ink: '#F5E8E2',
  muted: '#A8978F',
  blush: '#F7A1C4',
  danger: '#FF7A7A',
  pink: '#FB3C7F',
  champagne: '#FB3C7F',
  line: 'rgba(255,255,255,0.08)',
  card: 'rgba(255,255,255,0.04)',
};

function statusColor(status: string): string {
  if (status === 'ended_normal') return C.muted;
  if (status === 'ended_flagged') return C.danger;
  return C.blush;
}

const label: CSSProperties = { color: C.muted, fontSize: 12 };
const value: CSSProperties = { color: C.ink, fontSize: 12, fontWeight: 600, textAlign: 'right' };

function Kv({ k, v, testId }: { k: string; v: string; testId?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '3px 0' }}>
      <span style={label}>{k}</span>
      <span style={value} data-testid={testId}>
        {v}
      </span>
    </div>
  );
}

function Section({ children }: { children: string }) {
  return (
    <div
      style={{
        color: C.champagne,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        padding: '10px 0 4px',
      }}
    >
      {children}
    </div>
  );
}

const divider: CSSProperties = { height: 1, background: C.line, margin: '8px 0' };

function GiftLine({ g }: { g: CallGiftLine }) {
  const name = `${g.emoji ? `${g.emoji} ` : ''}${g.giftName} × ${g.quantity}`;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0' }}>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...value, textAlign: 'left' }}>{name}</span>
          {g.source === 'ask_gift' && (
            <span
              style={{
                color: C.blush,
                background: 'rgba(255,255,255,0.12)',
                borderRadius: 6,
                padding: '1px 6px',
                fontSize: 9,
                fontWeight: 700,
              }}
            >
              Ask Gift
            </span>
          )}
        </div>
        <div style={{ color: C.muted, fontSize: 11, marginTop: 1 }}>
          {g.unitEarningPaise == null ? 'Not recorded' : `${appRupees(g.unitEarningPaise)} each`}
        </div>
      </div>
      <span style={value}>{g.totalEarningPaise == null ? '—' : appRupees(g.totalEarningPaise)}</span>
    </div>
  );
}

/** The expanded half: call details, gifts received, call + gifts total. */
export function CallHistoryExpanded({
  row,
  onOpenTimeline,
}: {
  row: CallRowDetail;
  onOpenTimeline?: (callId: string) => void;
}) {
  const gifts = row.gifts;
  const callEarning = row.listenerSharePaise ?? '0';
  const grand =
    row.grandTotalEarningPaise ??
    (BigInt(callEarning) + BigInt(row.giftTotalEarningPaise ?? '0')).toString();
  // Older API (no outcome fields): keep the original "Ended by" line.
  const hasOutcome = row.outcome !== undefined;
  const inferred = row.attributionInferred ? ' (from lifecycle)' : '';
  return (
    <div data-testid={`call-expanded-${row.id}`} style={{ paddingTop: 10 }}>
      <div style={{ ...divider, margin: 0 }} />
      <Section>Call details</Section>
      {hasOutcome ? (
        <>
          <Kv k="Status" v={connectionLabel(row.outcome, row.connectedAt)} testId="call-connection" />
          <Kv k="Outcome" v={outcomeLabel(row.outcome)} testId="call-outcome" />
          <Kv k="Action by" v={`${actorLabel(row.outcomeActor)}${inferred}`} testId="call-actor" />
          <Kv
            k="Reason"
            v={outcomeReasonLabel(
              row.outcomeReason,
              row.outcomeActor,
              row.connectedAt != null,
              row.outcomeTrigger,
              row.ringDelivered,
            )}
            testId="call-reason"
          />
          <Kv k="Started" v={when(row.startedAt) ?? '—'} />
          <Kv k="Accepted" v={when(row.acceptedAt) ?? 'Not accepted'} testId="call-accepted" />
          <Kv k="Connected" v={when(row.connectedAt) ?? 'Never connected'} testId="call-connected" />
          <Kv k="Ended" v={when(row.endedAt) ?? '—'} />
        </>
      ) : (
        <>
          <Kv k="Status" v={callStatusLabel(row.status)} />
          <Kv k="Ended by" v={endedByLine(row.disconnectedBy, row.disconnectReason)} />
        </>
      )}
      <Kv k="Duration" v={appDuration(row.connectedAt ? row.durationSeconds : 0)} />
      <Kv k="Call earnings" v={appRupees(callEarning)} testId="call-value" />
      {/* Admin-only: the caller side of the same call. */}
      <Kv k="Caller paid" v={formatCoins(row.totalChargedCoins ?? 0)} testId="call-billing" />
      <Kv k="Call ID" v={row.id} />
      <Section>Gifts received</Section>
      {gifts === undefined ? (
        <span style={label}>Gift details not available</span>
      ) : gifts.length === 0 ? (
        <span style={label}>No gifts</span>
      ) : (
        gifts.map((g) => (
          <GiftLine key={`${g.giftCode}|${g.unitCoins}|${g.unitEarningPaise ?? ''}|${g.source ?? ''}`} g={g} />
        ))
      )}
      <div style={divider} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: C.ink, fontSize: 13, fontWeight: 700 }}>Call + Gifts total</span>
        <span style={{ color: C.ink, fontSize: 14, fontWeight: 800 }} data-testid="grand-total">
          {appRupees(grand)}
        </span>
      </div>
      {onOpenTimeline && (
        <div style={{ paddingTop: 10 }}>
          <Button size="small" onClick={() => onOpenTimeline(row.id)} data-testid={`call-timeline-${row.id}`}>
            View full timeline
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One call, collapsed: mode icon, @caller, "Xm Ys · Status", the listener's
 * earning, and a chevron. Click anywhere on the header to expand.
 */
export function CallHistoryCard({
  row,
  onOpenTimeline,
}: {
  row: CallRowDetail;
  onOpenTimeline?: (callId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const meta = [
    // When the attempt was placed — unconnected calls have no connect time.
    when(row.startedAt ?? row.connectedAt),
    row.mode === 'video' ? 'Video' : 'Audio',
    // Old Quick Connect rows keep their label; a fallback ring says so.
    row.isRandom
      ? 'Random'
      : row.callRequestId && (row.attemptNo ?? 1) > 1
        ? `Direct · fallback ring ${row.attemptNo}`
        : 'Direct',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div
      data-testid={`call-card-${row.id}`}
      style={{
        background: C.card,
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: 12,
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={open ? 'Collapse call details' : 'Expand call details'}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            flex: '0 0 40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: 17,
            background: 'linear-gradient(135deg, #7C3AED 0%, #FB3C7F 100%)',
          }}
        >
          {row.mode === 'video' ? <VideoCameraOutlined /> : <AudioOutlined />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: C.ink, fontSize: 14, fontWeight: 700 }}>
            {userLabel({ displayName: row.userDisplayName, handle: row.userHandle })}
          </div>
          {userSecondary({ displayName: row.userDisplayName, handle: row.userHandle }) && (
            <div style={{ color: C.muted, fontSize: 12, lineHeight: 1.3 }}>
              {userSecondary({ displayName: row.userDisplayName, handle: row.userHandle })}
            </div>
          )}
          {row.outcome !== undefined ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 3 }}>
              <Tag color={outcomeColor(row.outcome)} style={{ marginInlineEnd: 0 }} data-testid={`call-outcome-tag-${row.id}`}>
                {outcomeLabel(row.outcome)}
              </Tag>
              {row.outcome !== 'in_progress' && (
                <span style={{ color: C.muted, fontSize: 11 }} data-testid={`call-actor-line-${row.id}`}>
                  by {actorLabel(row.outcomeActor)} · {appDuration(row.connectedAt ? row.durationSeconds : 0)}
                </span>
              )}
            </div>
          ) : (
            <div style={{ color: statusColor(row.status), fontSize: 11, marginTop: 2 }}>
              {appDuration(row.connectedAt ? row.durationSeconds : 0)} · {callStatusLabel(row.status)}
            </div>
          )}
          {meta && <div style={{ color: C.muted, fontSize: 11, marginTop: 2 }}>{meta}</div>}
        </div>
        <span style={{ color: C.ink, fontWeight: 800 }} data-testid={`call-earning-${row.id}`}>
          {appRupees(row.listenerSharePaise ?? '0')}
        </span>
        <span style={{ color: C.muted, fontSize: 12 }}>{open ? <UpOutlined /> : <DownOutlined />}</span>
      </div>
      {open && <CallHistoryExpanded row={row} onOpenTimeline={onOpenTimeline} />}
    </div>
  );
}
